// docs/119 — levels by class and species, read from the sim's own catalogue (`fk-sim-wasm`
// `progression()`) and walked by the same function the module validates picks with
// (`progression_walk`), so the level-up and path screens can never offer what the server refuses.
import { wasmModule } from './wasm';
import type { WasmAbility } from './actions';
import { describeAbility } from './describe';
import { powerLine, rustPower } from '../items/describe';

/** A level step as serde writes it: `"Learn"`, `{ Gain: 1010 }`, `{ Choose: [..] }`, `{ Points: 1 }`. */
export type Step = 'Learn' | 'Secrets' | 'Path' | 'PathFeature' | 'Heritage' | { Gain: number } | { Choose: number[] } | { Points: number };
export type StepKind = 'gain' | 'choose' | 'learn' | 'secrets' | 'path' | 'pathFeature' | 'heritage' | 'points';

export interface Lines { hp: number; guard: number; move_tiles: number; power: number; focus: number; resource: number; pierce: number; range: number }
export interface Feature {
  id: number; name: string; blurb: string; rules: string; lines: Lines; powers: unknown[]; abilities: number[]; replaces: number[]; points: number;
  pet: Lines; pet_powers: unknown[];
}
export interface ClassTrack {
  class: string; levels: Step[][]; library: { ability: number; from: number }[]; paths: { id: number; features: number[] }[];
  talents: Talent[]; features: Feature[]; abilities: WasmAbility[];
}
export interface Talent { feature: number; cost: number; level: number; needs: unknown[] }
export interface Training { id: number; name: string; blurb: string; per_rank: Lines; powers: unknown[]; costs: number[]; level: number }
export interface Look { ability: number; element: string; form: string | null }
export interface Catalogue {
  classes: ClassTrack[]; species: { species: string; heritage: unknown[]; talents: Talent[] }[]; training: Training[]; talents: Talent[];
  laurels: { feature: number; most: number }[]; features: Feature[]; looks: Look[];
  path_levels: number[]; heritage_levels: number[]; track_top: number; max_level: number; points_per_level: number; points_per_laurel: number; laurel_xp: number;
}
export interface SlotView { level: number; slot: number; step: Step; options: number[]; value: number | null; chosen: boolean }
export interface Granted { lines: Lines; powers: unknown[]; abilities: number[]; replaces: number[]; features: number[]; pet: Lines; pet_powers: unknown[] }
export interface Walk {
  slots: SlotView[]; granted: Granted; earned: number; spent: number; banked: number; abilities: number[];
  talents: { feature: number; cost: number; level: number; taken: boolean; why_not?: string | null }[];
  training: { id: number; ranks: number; why_not?: string | null }[];
}

/** The stored row's shape (`hero_progress`), or nothing for a hero who never chose. */
export interface ProgressRow { picks: readonly number[]; training: readonly number[] | Uint8Array; talents: readonly number[]; laurels: readonly number[] }

export const TRACK_TOP = 20;
export const MAX_LEVEL = 99;
export const LAUREL_XP = 2000;

/** `xp_to_reach` (fk_grammar::progression): 100·L·(L−1)/2 to 20, then a flat step a laurel level. */
export const xpToReach = (level: number): number => {
  const l = Math.min(level, TRACK_TOP);
  return (100 * l * (l - 1)) / 2 + Math.max(0, level - TRACK_TOP) * LAUREL_XP;
};

let cat: Catalogue | undefined;
export function catalogue(): Catalogue {
  cat ??= wasmModule().progression() as Catalogue;
  return cat;
}

let byId: Map<number, Feature> | undefined;
export function featureOf(id: number): Feature | undefined {
  byId ??= new Map(catalogue().features.map((f) => [f.id, f]));
  return byId.get(id);
}

export function stepKind(s: Step): StepKind {
  if (typeof s === 'string') return ({ Learn: 'learn', Secrets: 'secrets', Path: 'path', PathFeature: 'pathFeature', Heritage: 'heritage' } as const)[s];
  if ('Gain' in s) return 'gain';
  if ('Choose' in s) return 'choose';
  return 'points';
}

/** A slot the player chooses (a Learn's ability, a Choose's or a heritage's feature, the path). */
export const isChoice = (v: SlotView): boolean => {
  const k = stepKind(v.step);
  return k === 'heritage' ? v.options.length > 1 : ['choose', 'learn', 'secrets', 'path'].includes(k);
};
/** A choice still open: it holds its default until the player picks. */
export const isOpen = (v: SlotView): boolean => isChoice(v) && !v.chosen && v.options.length > 0;
/** A slot whose value is an ability id (else a feature id, or a point count). */
export const holdsAbility = (v: SlotView): boolean => ['learn', 'secrets'].includes(stepKind(v.step));

/** What a step is called on the level-up and path screens. */
export function stepTitle(v: SlotView, classId: string): string {
  switch (stepKind(v.step)) {
    case 'learn': return classId === 'wizard' ? 'Learn a spell' : ['cleric', 'druid', 'bard', 'paladin'].includes(classId) ? 'Learn a prayer or spell' : 'Learn a technique';
    case 'secrets': return 'Magical Secrets — learn from any class';
    case 'path': return 'Choose your path';
    case 'pathFeature': return 'Your path';
    case 'heritage': return v.options.length > 1 ? 'Heritage — choose one' : 'Heritage';
    case 'choose': return 'Choose one';
    case 'points': return 'Skill points';
    default: return 'Class feature';
  }
}

const SIGNED = (n: number) => (n > 0 ? `+${n}` : `${n}`);
/** A feature's flat numbers in words ("+4 max HP", "+1 guard"). */
export function linesWords(l: Lines, resource = 'resource'): string[] {
  const out: string[] = [];
  if (l.hp) out.push(`${SIGNED(l.hp)} max HP`);
  if (l.guard) out.push(`${SIGNED(l.guard)} guard`);
  if (l.move_tiles) out.push(`${SIGNED(l.move_tiles)} move`);
  if (l.power) out.push(`${SIGNED(l.power)} power (weapon damage)`);
  if (l.focus) out.push(`${SIGNED(l.focus)} focus (spells and heals)`);
  if (l.resource) out.push(`${SIGNED(l.resource)} ${resource} maximum`);
  if (l.pierce) out.push(`${SIGNED(l.pierce)} guard pierce`);
  if (l.range) out.push(`${SIGNED(l.range)} range for shots`);
  return out;
}

let abilityCat: WasmAbility[] | undefined;
export function abilityOf(id: number): WasmAbility | undefined {
  abilityCat ??= wasmModule().abilities() as WasmAbility[];
  return abilityCat.find((a) => a.id === id);
}

/** A power in plain words, with no `%` (11 §10.4: no percentages in level-up copy): a share of
 * gold, experience or prices is said as "in every 100". */
export function powerWords(p: unknown): string {
  const spec = rustPower(p) as Record<string, unknown>;
  const [k] = Object.keys(spec);
  const v = Number(spec[k!]);
  switch (k) {
    case 'purse': return `${v} more gold in every 100 from every fight`;
    case 'lore': return `${v} more experience in every 100`;
    case 'haggle': return `Village prices ${v} in every 100 better for you, buying and selling`;
    default: return powerLine(spec as Parameters<typeof powerLine>[0]).replace(/%/g, ' in every 100');
  }
}

/** Every line a feature's card shows: its rules, numbers, powers, abilities, what it replaces, points, a companion's share. */
export function featureLines(f: Feature, resource = 'resource'): string[] {
  const out: string[] = [];
  if (f.rules) out.push(f.rules);
  out.push(...linesWords(f.lines, resource));
  for (const p of f.powers) out.push(powerWords(p));
  for (const id of f.abilities) {
    const a = abilityOf(id);
    if (a) out.push(`${a.slot === 'Passive' ? 'Passive' : 'New ability'} — ${a.name}: ${describeAbility(a)}`);
  }
  for (const id of f.replaces) {
    const a = abilityOf(id);
    if (a) out.push(`Takes the place of ${a.name}`);
  }
  if (f.points) out.push(`+${f.points} skill point${f.points === 1 ? '' : 's'}`);
  const pet = linesWords(f.pet);
  if (pet.length || f.pet_powers.length) out.push(`Your companion: ${[...pet, ...f.pet_powers.map((p) => powerWords(p).toLowerCase())].join('; ')}`);
  return out;
}

/** One option of a slot as a card: its name, blurb and lines (an ability's or a feature's). */
export interface OptionCard { id: number; name: string; blurb: string; lines: string[]; ability?: WasmAbility; from?: number; tag?: string }
export function optionCard(v: SlotView, id: number, classId: string, resource = 'resource'): OptionCard {
  if (holdsAbility(v)) {
    const a = abilityOf(id);
    const owner = a?.class && a.class.toLowerCase() !== classId ? a.class : undefined;
    const lib = catalogue().classes.flatMap((c) => c.library).find((l) => l.ability === id);
    return {
      id, name: a?.name ?? `Ability ${id}`, blurb: a ? `${a.slot === 'Bonus' ? 'Bonus action' : a.slot === 'Passive' ? 'Passive' : 'Action'}${a.resource_cost ? ` · ${a.resource_cost} ${resource}` : ''}${a.cooldown ? ` · cooldown ${a.cooldown}` : ''}` : '',
      lines: a ? [describeAbility(a)] : [], ability: a, from: lib?.from, tag: owner,
    };
  }
  const f = featureOf(id);
  return { id, name: f?.name ?? `Feature ${id}`, blurb: f?.blurb ?? '', lines: f ? featureLines(f, resource) : [] };
}

/** The hero's walk: every slot to 20 (the levels to come as their defaults), what they have now, their points. */
export function heroWalk(cls: number, species: number, level: number, pick: number, spells: readonly number[], row?: ProgressRow): Walk {
  return wasmModule().progression_walk(
    cls, species, level, pick, Uint16Array.from(spells),
    Uint16Array.from(row?.picks ?? []), Uint8Array.from(row?.training ?? []), Uint16Array.from(row?.talents ?? []), Uint16Array.from(row?.laurels ?? []),
  ) as Walk;
}

/** The `[slot, value, …]` a `take_level` sends for the slots picked on the level-up screen. */
export const picksFlat = (picked: ReadonlyMap<number, number>): number[] => [...picked.entries()].sort((a, b) => a[0] - b[0]).flatMap(([s, v]) => [s, v]);

/** The class's three paths, for the path card. */
export const pathsOf = (cls: number) => catalogue().classes[cls]?.paths ?? [];

/** A level ability's look (docs/119), for the spell renderer when it has no hand-written one. */
export const levelLook = (ability: number): Look | undefined => catalogue().looks.find((l) => l.ability === ability);
